package data.objects.views.directediting

import apiclient.FormationClient
import apiclient.geoobjects.ChangeTask
import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.MeetingInvitationStatus
import apiclient.geoobjects.ObjectChanges
import apiclient.geoobjects.TaskState
import apiclient.geoobjects.applyObjectChanges
import apiclient.geoobjects.changeInviteStatus
import apiclient.users.UserProfileSummary
import apiclient.users.toUserProfileSummary
import auth.ApiUserStore
import data.ObjectAndUserHandler
import data.objects.ActiveObjectStore
import data.objects.views.dotNumber
import data.objects.views.iconNumber
import data.users.UserListStore
import data.users.views.initials
import dev.fritz2.core.RenderContext
import dev.fritz2.core.RootStore
import dev.fritz2.core.SimpleHandler
import dev.fritz2.core.Store
import dev.fritz2.core.storeOf
import koin.withKoin
import kotlin.time.Duration.Companion.hours
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import localization.TL
import localization.getString
import localization.getTranslationFlow
import localization.translate
import mapsearch.updateInCaches
import model.L
import model.isInternalAdminGroupAdminOrGroupOwner
import overlays.BusyStore
import search.simpleUserProfileListEntry
import search.titleSubtitleStack
import theme.FormationColors
import theme.FormationIcons
import theme.FormationUIIcons
import twcomponents.meetingStateIndicator
import twcomponents.taskStateIndicator
import twcomponents.twIconCustomSize
import twcomponents.twIconLarge
import twcomponents.twIconMedium
import twcomponents.twManageAttendeeModal
import twcomponents.twMeetingDateTimeModal
import twcomponents.twMeetingInvitationStateModal
import twcomponents.twRowOfNoGap
import twcomponents.twTaskDueDateModal
import twcomponents.twTaskStateSelectModal
import twcomponents.twUserSelectModal
import utils.apiScope
import utils.formatEventTimeSpan
import utils.getName
import utils.getSubtitle
import utils.getTitle
import utils.merge
import webcomponents.twEllipseText
import webcomponents.userOrStateIcon

const val SELECT_BUTTON_STYLE = "cursor-pointer hover:bg-gray-100 text-gray-400 hover:text-gray-600" // border border-dashed border-gray-200

fun RenderContext.twTaskAssignmentButton() {
    withKoin {
        val busyStore: BusyStore = get()
        val formationClient: FormationClient = get()
        val apiUserStore: ApiUserStore = get()
        val objectAndUserHandler: ObjectAndUserHandler = get()
        val activeObjectStore: ActiveObjectStore = get()
        val assigneeId = activeObjectStore.map(GeoObjectDetails.L.assignedTo)
        val owner = activeObjectStore.map(GeoObjectDetails.L.ownerId)
        val selectedUserStore: Store<UserProfileSummary?> = storeOf(activeObjectStore.current.assignedToUser, Job())
        val userSelectModalToggleStore: Store<Boolean> = storeOf(false, Job())

        twUserSelectModal(
            toggleShowModalStore = userSelectModalToggleStore,
            titleAsFlow = flowOf("Task Assignment"), // TODO translate
            selectedUserProfileSummaryStore = selectedUserStore,
            additionalSelectedUserHandlers = listOf(
                SimpleHandler { data, _ ->
                    data handledBy { userProfileSummary ->
                        // Assign User or UnAssign if null
                        if (assigneeId.current != userProfileSummary?.userId) {
                            busyStore.handleApiCall(
                                supplier = suspend {
                                    formationClient.applyObjectChanges(
                                        ObjectChanges(
                                            activeObjectStore.current.id,
                                            if (userProfileSummary != null) {
                                                if (apiUserStore.current.userId == userProfileSummary.userId && assigneeId.current.isNullOrBlank()) {
                                                    ChangeTask.AssignSelf()
                                                } else {
                                                    ChangeTask.AssignOther(userProfileSummary.userId)
                                                }
                                            } else {
                                                // if selected is null -> unassign self or other user
                                                if (apiUserStore.current.userId == assigneeId.current) {
                                                    ChangeTask.UnAssignSelf
                                                } else {
                                                    ChangeTask.UnAssignOther
                                                }
                                            },
                                        ),
                                    )
                                },
                                successMessage = if (userProfileSummary != null) {
                                    TL.AlertNotifications.TASK_ASSIGNED_TO_USER.getTranslationFlow(
                                        mapOf(
                                            "firstname" to (userProfileSummary.firstName ?: ""),
                                            "lastname" to (userProfileSummary.lastName ?: ""),
                                        ),
                                    )
                                } else TL.AlertNotifications.TASK_UNASSIGNED.getTranslationFlow(),
                                processResult = { results ->
                                    results.firstOrNull()?.let { obj ->
                                        activeObjectStore.updateActiveObject(obj)
                                        updateInCaches(obj)
                                    }
                                },
                                processError = { e ->
                                    console.error(e)
                                },
                            )
                        }
                    }
                },
            ),
            anyoneOption = true,
        )

        combine(owner.data, assigneeId.data, selectedUserStore.data) { o, a, usr -> Triple(o, a, usr) }.render { (owner, assignee, userProfileSummary) ->
            div(
                "flex flex-row items-center w-full justify-between " +
                    "rounded-xl overflow-hidden",
            ) {
                val currentUser = apiUserStore.current
                val canChangeAssignee = owner == currentUser.userId || currentUser.isInternalAdminGroupAdminOrGroupOwner()
                val canRemoveSelf = assignee == currentUser.userId
                val canAssignSelf = assignee.isNullOrBlank() && !currentUser.isAnonymous

                div("text-formationBlack") {
                    simpleUserProfileListEntry(
                        userProfileSummary?.copy(
                            firstName = userProfileSummary.firstName?.let {
                                TL.CardInfo.ASSIGNED_TO.getString("user" to it)
                            } ?: userProfileSummary.firstName,
                            jobTitle = if (canRemoveSelf) {
                                TL.CardUserSelect.CLICK_TO_UNASSIGN_SELF.getString()
                            } else userProfileSummary.jobTitle,
                        ) ?: UserProfileSummary(
                            userId = "",
                            firstName = TL.CardInfo.ASSIGNED_TO.getString("user" to TL.CardUserSelect.ANYONE.getString()),
                            lastName = null,
                            jobTitle = if (canChangeAssignee) {
                                TL.CardUserSelect.ANYONE_CLICK_TO_ASSIGN_SOMEONE.getString()
                            } else if (canAssignSelf) {
                                TL.CardUserSelect.ANYONE_CLICK_TO_ASSIGN_SELF.getString()
                            } else {
                                TL.CardUserSelect.ANYONE_DESCRIPTION.getString()
                            },
                        ),
                        fallbackIcon = FormationIcons.UserGroup,
                    )
                }
                if (canChangeAssignee) {
                    className(SELECT_BUTTON_STYLE)
                    div("pr-2") {
                        twIconMedium(FormationUIIcons.Select)
                    }
                    clicks handledBy {
                        userSelectModalToggleStore.update(true)
                    }
                } else if (canAssignSelf || canRemoveSelf) {
                    className(SELECT_BUTTON_STYLE)
                    div("pr-2") {
                        twIconMedium(if (canAssignSelf) FormationUIIcons.Check else FormationUIIcons.Close)
                    }
                    // Directly Assign or UnAssign self
                    clicks handledBy {
                        busyStore.handleApiCall(
                            supplier = suspend {
                                formationClient.applyObjectChanges(
                                    ObjectChanges(
                                        activeObjectStore.current.id,
                                        if (canAssignSelf) ChangeTask.AssignSelf() else ChangeTask.UnAssignSelf,
                                    ),
                                )
                            },
                            successMessage = if (canAssignSelf) {
                                TL.AlertNotifications.TASK_ASSIGNED_TO_USER.getTranslationFlow(
                                    args = mapOf(
                                        "firstname" to TL.General.YOU.getString(
                                            mapOf("case" to "dative"),
                                        ),
                                        "lastname" to "",
                                    ),
                                )
                            } else TL.AlertNotifications.TASK_UNASSIGNED.getTranslationFlow(),
                            processResult = { results ->
                                results.firstOrNull()?.let { obj ->
                                    selectedUserStore.update(obj.assignedToUser)
                                    activeObjectStore.updateActiveObject(obj)
                                    updateInCaches(obj)
                                }
                            },
                            processError = { e ->
                                console.error(e)
                            },
                        )
                    }
                } else if (userProfileSummary != null) {
                    className(SELECT_BUTTON_STYLE)
                    clicks handledBy {
                        // Open User Profile
                        objectAndUserHandler.updateAndLocateActiveUserById(userProfileSummary.userId)
                    }
                }
            }
        }
    }
}

fun RenderContext.twTaskStatusButton() {
    withKoin {
        val busyStore: BusyStore = get()
        val formationClient: FormationClient = get()
        val apiUserStore: ApiUserStore = get()
        val activeObjectStore: ActiveObjectStore = get()
        val currentTaskState = activeObjectStore.map(GeoObjectDetails.L.taskState)
        val assignee = activeObjectStore.map(GeoObjectDetails.L.assignedTo)
        val owner = activeObjectStore.map(GeoObjectDetails.L.ownerId)

        val modalToggleStore: Store<Boolean> = storeOf(false, Job())

        twTaskStateSelectModal(
            toggleShowModalStore = modalToggleStore,
            selectedTaskStateHandler = SimpleHandler { data, _ ->
                data handledBy { taskState ->
                    if (currentTaskState.current != taskState) {
                        // Update task due date
                        apiScope.launch {
                            busyStore.handleApiCall(
                                supplier = suspend {
                                    formationClient.applyObjectChanges(
                                        ObjectChanges(
                                            activeObjectStore.current.id,
                                            ChangeTask.SetState(taskState),
                                        ),
                                    )
                                },
                                successMessage = TL.AlertNotifications.OBJECT_SUCCESSFULLY_UPDATED.getTranslationFlow(
                                    mapOf(
                                        "object" to activeObjectStore.current.objectType.getName(),
                                    ),
                                ),
                                processResult = { results ->
                                    results.firstOrNull()?.let { obj ->
                                        activeObjectStore.updateActiveObject(obj)
                                        updateInCaches(obj)
                                    }
                                },
                                processError = { e ->
                                    console.error(e)
                                },
                            )
                        }
                    }
                }
            },
        )


        combine(owner.data, assignee.data, currentTaskState.data) { o, a, t ->
            Triple(o, a, (t ?: TaskState.UnAssigned))
        }.render { (ownerId, assigneeId, taskState) ->
            div(
                "flex flex-row items-center w-full min-w-60 " +
                    "grow shrink basis-60 justify-between rounded-xl overflow-hidden " +
                    "gap-2 p-2",
            ) {
                val currentUser = apiUserStore.current
                val canChangeTaskStatus =
                    taskState != TaskState.UnAssigned && (ownerId == currentUser.userId || currentUser.isInternalAdminGroupAdminOrGroupOwner() || assigneeId == currentUser.userId)
                taskStateIndicator(taskState)
                titleSubtitleStack("text-formationBlack") {
                    twEllipseText("w-full text-sm font-bold") {
                        TL.CardInfo.STATUS.getTranslationFlow().merge(flowOf(taskState.getTitle())).renderText(into = this)
                    }
                    twEllipseText("w-full text-xs font-light") {
                        twRowOfNoGap("items-center") {
//                            colorDot(taskState.getColor().color)
                            +taskState.getSubtitle()
                        }
                    }
                }

                if (canChangeTaskStatus) {
                    className(SELECT_BUTTON_STYLE)
                    twIconMedium(FormationUIIcons.Select)
                    clicks handledBy {
                        modalToggleStore.update(true)
                    }
                }
            }
        }
    }
}

fun RenderContext.twTaskDueDateButton() {
    withKoin {
        val busyStore: BusyStore = get()
        val formationClient: FormationClient = get()
        val apiUserStore: ApiUserStore = get()
        val activeObjectStore: ActiveObjectStore = get()
        val startTime = activeObjectStore.map(GeoObjectDetails.L.atTime)
        val assignee = activeObjectStore.map(GeoObjectDetails.L.assignedTo)
        val owner = activeObjectStore.map(GeoObjectDetails.L.ownerId)

        val modalToggleStore: Store<Boolean> = storeOf(false, Job())

        twTaskDueDateModal(
            toggleShowModalStore = modalToggleStore,
            selectedDueDateHandler = SimpleHandler { data, _ ->
                data handledBy { dueDate ->
                    // Update task due date
                    apiScope.launch {
                        busyStore.handleApiCall(
                            supplier = suspend {
                                formationClient.applyObjectChanges(
                                    ObjectChanges(
                                        activeObjectStore.current.id,
                                        ChangeTask.SetAtTime(dueDate),
                                    ),
                                )
                            },
                            successMessage = TL.AlertNotifications.OBJECT_SUCCESSFULLY_UPDATED.getTranslationFlow(
                                mapOf(
                                    "object" to activeObjectStore.current.objectType.getName(),
                                ),
                            ),
                            processResult = { results ->
                                results.firstOrNull()?.let { obj ->
                                    activeObjectStore.updateActiveObject(obj)
                                    updateInCaches(obj)
                                }
                            },
                            processError = { e ->
                                console.error(e)
                            },
                        )
                    }
                }
            },
        )


        combine(owner.data, assignee.data, startTime.data) { o, a, t ->
            Triple(o, a, t)
        }.render { (ownerId, assigneeId, time) ->
            div("flex flex-row items-center w-full min-w-48 grow shrink basis-48 justify-between rounded-xl overflow-hidden gap-1 p-2") {
                val currentUser = apiUserStore.current
                val canChangeDueDate =
                    ownerId == currentUser.userId || currentUser.isInternalAdminGroupAdminOrGroupOwner() //|| assigneeId == currentUser.userId

                titleSubtitleStack("text-formationBlack") {
                    twEllipseText("text-sm font-bold") {
                        translate(TL.CardInfo.DUE_DATE)
                    }
                    twEllipseText("text-xs font-light") {
                        val now = Clock.System.now()
                        if (time != null && time.toEpochMilliseconds() in (now - 1.hours)
                                .toEpochMilliseconds()..(now + 1.hours)
                                .toEpochMilliseconds()
                        ) {
                            className("text-red-500")
                        }
                        formatEventTimeSpan(
                            startTime.data,
                            flowOf(null),
                        ).renderText(into = this)
                    }
                }

                if (canChangeDueDate) {
                    className(SELECT_BUTTON_STYLE)
                    twIconMedium(FormationUIIcons.Select)
                    clicks handledBy {
                        modalToggleStore.update(true)
                    }
                }
            }
        }
    }
}

open class ValueSelectionStore<T>(init: Set<T>) : RootStore<Set<T>?>(
    initialData = init,
    job = Job(),
) {

    val reset = handle {
        null
    }

    val addOrRemoveValue = handle<T> { current, newUser ->
        if (current.isNullOrEmpty() || !current.contains(newUser)) {
            current.orEmpty() + newUser
        } else {
            current.minus(newUser).ifEmpty { null }
        }
    }

    val addAllValues = handle<List<T>> { current, list ->
        if (current.isNullOrEmpty() || !current.containsAll(list)) {
            list.toSet()
        } else {
            null
        }
    }

}

fun RenderContext.meetingAttendeesButton() {
    withKoin {
        val activeObjectStore: ActiveObjectStore = get()
        val apiUserStore: ApiUserStore = get()
        val userListStore: UserListStore = get()
        val attendees = activeObjectStore.map(GeoObjectDetails.L.attendees)
        val owner = activeObjectStore.map(GeoObjectDetails.L.ownerId)
        val userSelectModalToggleStore: Store<Boolean> = storeOf(false, Job())

        twManageAttendeeModal(
            toggleShowModalStore = userSelectModalToggleStore,
        )

        div(
            "flex flex-row items-center w-full justify-between " +
                "rounded-xl overflow-hidden gap-2 p-2",
        ) {
            className(
                attendees.data.map { attendeeList ->
                    if (!attendeeList.isNullOrEmpty() && !apiUserStore.current.isAnonymous) {
                        "cursor-pointer hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                    } else ""
                },
            )
            attendees.data.render { attendeeList ->
                div("text-formationBlack") {
                    twIconLarge(icon = FormationIcons.UserGroup)
                }
                if (!attendeeList.isNullOrEmpty()) {
                    val userProfileSummaries = attendeeList.mapNotNull { attendee ->
                        userListStore.getPublicUserProfile(attendee.userId)?.toUserProfileSummary()
                    }

                    titleSubtitleStack("text-formationBlack") {
                        // display total number of attendees
                        twEllipseText("font-bold text-sm") {
                            translate(TL.CardInfo.ATTENDEES, mapOf("size" to attendeeList.size))
                        }

                        // display meeting invitation statuses as subtitle
                        val pending = attendeeList.filter {
                            it.meetingInvitationStatus == MeetingInvitationStatus.Pending
                        }.size
                        val accepted = attendeeList.filter {
                            it.meetingInvitationStatus == MeetingInvitationStatus.Accepted
                        }.size
                        val maybe = attendeeList.filter {
                            it.meetingInvitationStatus == MeetingInvitationStatus.Maybe
                        }.size
                        val rejected = attendeeList.filter {
                            it.meetingInvitationStatus == MeetingInvitationStatus.Rejected
                        }.size

                        twRowOfNoGap("flex-grow-0 flex-shrink-0 items-center justify-even text-xs overflow-x-hidden") {
                            iconNumber(pending, icon = FormationIcons.Wait)
                            dotNumber(accepted, FormationColors.GreenActive.color)
                            dotNumber(maybe, FormationColors.YellowDoing.color)
                            dotNumber(rejected, FormationColors.RedError.color)
                        }
                    }

                    // display the first 5 profilePictures of attendees
                    twRowOfNoGap("items-center gap-1") {
                        val prev = 5
                        userProfileSummaries.take(prev).forEach { profile ->
                            userOrStateIcon(
                                32.0,
                                picture = profile.profilePhoto?.href,
                                initials = initials(profile.firstName, profile.lastName),
                                iconSize = { smaller },
                            )
                        }
                        if (userProfileSummaries.size > prev) {
                            +"+ ${userProfileSummaries.size - prev}"
                        }
                        div("pl-2") {
                            twIconMedium(FormationUIIcons.Select)
                        }
                    }
                    clicks handledBy {
                        userSelectModalToggleStore.update(true)
                    }
                } else {
                    titleSubtitleStack {
                        twEllipseText("text-sm font-bold") {
                            translate(
                                TL.CardInfo.ATTENDEES,
                                mapOf(
                                    "size" to TL.General.NONE.getString(),
                                ),
                            )
                        }
                        owner.data.render { ownerId ->
                            val currentUser = apiUserStore.current
                            val canChangeAttendees = ownerId == currentUser.userId || currentUser.isInternalAdminGroupAdminOrGroupOwner()
                            if (canChangeAttendees) {
                                twEllipseText("text-xs font-light") {
                                    translate(TL.CardInfo.CLICK_TO_INVITE)
                                }
                            }
                        }
                    }
                    if (!apiUserStore.current.isAnonymous) {
                        twIconMedium(FormationUIIcons.Select)
                        clicks handledBy {
                            userSelectModalToggleStore.update(true)
                        }
                    }
                }
            }
        }
    }
}

fun RenderContext.twMeetingInvitationStatusButton() {
    withKoin {
        val busyStore: BusyStore = get()
        val formationClient: FormationClient = get()
        val apiUserStore: ApiUserStore = get()
        val activeObjectStore: ActiveObjectStore = get()
        val attendees = activeObjectStore.map(GeoObjectDetails.L.attendees)
        val currentUser = apiUserStore.current
        val modalToggleStore: Store<Boolean> = storeOf(false, Job())

        twMeetingInvitationStateModal(
            toggleShowModalStore = modalToggleStore,
            selectedInvitationStateHandler = SimpleHandler { data, _ ->
                data handledBy { meetingState ->
                    // Update meeting invitation state
                    apiScope.launch {
                        busyStore.handleApiCall(
                            supplier = suspend {
//                                formationClient.applyObjectChanges(
//                                    ObjectChanges(
//                                        activeObjectStore.current.id,
//                                        .SetState(meetingState),
//                                    ),
//                                )
                                formationClient.changeInviteStatus(
                                    activeObjectStore.current.id,
                                    currentUser.userId,
                                    meetingState,
                                )
                            },
                            successMessage = TL.AlertNotifications.OBJECT_SUCCESSFULLY_UPDATED.getTranslationFlow(
                                mapOf(
                                    "object" to activeObjectStore.current.objectType.getName(),
                                ),
                            ),
                            processResult = { result ->
//                                result.firstOrNull()?.let { obj ->
//                                    activeObjectStore.updateActiveObject(obj)
//                                    insertInCaches(obj)
//                                }
                                activeObjectStore.updateActiveObject(result)
                                updateInCaches(result)
                            },
                            processError = { e ->
                                console.error(e)
                            },
                        )
                    }
                }
            },
        )


        attendees.data.render { attendeeList ->
            val usersStatus = attendeeList?.firstOrNull {
                it.userId == apiUserStore.current.userId
            }?.meetingInvitationStatus

            usersStatus?.let { usersStatus ->
                div(
                    "flex flex-row items-center w-full min-w-60 grow " +
                        "shrink basis-60 justify-between rounded-xl cursor-pointer " +
                        "overflow-hidden gap-2 p-2 " +
//                        "border border-dashed border-gray-200" +
                        "text-gray-400 hover:bg-gray-100 hover:text-gray-600",
                ) {
                    meetingStateIndicator(usersStatus)
                    titleSubtitleStack("text-formationBlack") {
                        twEllipseText("w-full text-sm font-bold") {
                            TL.CardInfo.STATUS.getTranslationFlow().merge(flowOf(usersStatus.getName())).renderText(into = this)
                        }
                        twEllipseText("w-full text-xs font-light") {
                            translate(TL.CardInfo.SET_YOUR_MEETING_STATUS)
                        }
                    }
                    twIconMedium(FormationUIIcons.Select)
                    clicks handledBy {
                        modalToggleStore.update(true)
                    }
                }
            }
        }
    }
}

fun RenderContext.twMeetingDateTimeButton() {
    withKoin {
        val busyStore: BusyStore = get()
        val formationClient: FormationClient = get()
        val apiUserStore: ApiUserStore = get()
        val activeObjectStore: ActiveObjectStore = get()
        val startTime = activeObjectStore.map(GeoObjectDetails.L.atTime)
        val endTime = activeObjectStore.map(GeoObjectDetails.L.endTime)
        val owner = activeObjectStore.map(GeoObjectDetails.L.ownerId)

        val modalToggleStore: Store<Boolean> = storeOf(false, Job())

        twMeetingDateTimeModal(
            toggleShowModalStore = modalToggleStore,
            selectedAtTimeAndDurationHandler = SimpleHandler { data, _ ->
                data handledBy { (atTime, duration) ->
                    apiScope.launch {
//                        busyStore.handleApiCall(
//                            supplier = suspend {
//                                formationClient.applyObjectChanges(
//                                    ObjectChanges(
//                                        activeObjectStore.current.id,
//                                        ChangeTask.SetAtTime(atTime),
//                                    ),
//                                )
//                            },
//                            successMessage = TL.AlertNotifications.OBJECT_SUCCESSFULLY_UPDATED.getTranslationFlow(
//                                mapOf(
//                                    "object" to activeObjectStore.current.objectType.getName(),
//                                ),
//                            ),
//                            processResult = { results ->
//                                results.firstOrNull()?.let { obj ->
//                                    activeObjectStore.updateActiveObject(obj)
//                                    insertInCaches(obj)
//                                }
//                            },
//                            processError = { e ->
//                                console.error(e)
//                            },
//                        )
                    }
                }
            },
        )

        combine(owner.data, startTime.data) { o, t ->
            Pair(o, t)
        }.render { (ownerId, time) ->
            div(
                "flex flex-row items-center w-full min-w-52 grow " +
                    "shrink basis-52 justify-between rounded-xl gap-1 p-2",
            ) {
                val currentUser = apiUserStore.current
                val canChangeDateTime =
                    ownerId == currentUser.userId || currentUser.isInternalAdminGroupAdminOrGroupOwner()

                twRowOfNoGap("min-h-9 items-center justify-center gap-2 text-formationBlack") {
                    twIconCustomSize(icon = FormationIcons.Time, "32px")
                    p("text-xs font-bold line-clamp-2") {
                        val now = Clock.System.now()
                        if (time != null && time.toEpochMilliseconds() in (now - 1.hours)
                                .toEpochMilliseconds()..(now + 1.hours)
                                .toEpochMilliseconds()
                        ) {
                            className("text-red-500")
                        }
                        formatEventTimeSpan(
                            startTime.data,
                            endTime.data,
                        ).renderText(into = this)
                    }
                }

                if (canChangeDateTime) {
                    className(
                        "cursor-pointer " +
//                        "border border-dashed border-gray-200 " +
                            "text-gray-400 hover:bg-gray-100 hover:text-gray-600",
                    )
                    twIconMedium(FormationUIIcons.Select)
                    clicks handledBy {
                        modalToggleStore.update(true)
                    }
                }
            }
        }
    }
}
